import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { delay, finalize, retryWhen, take, tap } from 'rxjs/operators';
import { PagingService } from '../paging/paging.service';
import { IPrintJob } from '../print-job/models/print-job.model';
import { TenantService } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { IPrinterEmbedded } from './models/printer-embedded-model';
import { IPrinter } from './models/printer.model';
import { IQueue } from './models/queue.model';
import * as _ from 'lodash';
import { Logger, LoggingService } from 'ionic-logging-service';
import { SanitizationService } from '../sanitization/sanitization.service';

@Injectable({
  providedIn: 'root'
})
export class PrinterService {

  private patchObserver: Observer<IPrinter> = null;
  public  user = this.userService.userSignal;
  private printerListPageObject: any;
  private printersFromQueuesRequest: any;
  private PRINTER: string = 'PRINTER';
  private httpErrorResponse: HttpErrorResponse;
  public tenant = this.tenantService.tenantSignal;
  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private pagingService: PagingService,
    private loggingService: LoggingService,
    private tenantService: TenantService,
    private userService: UserService,
    private sanitizationService: SanitizationService
    ) {
      this.logger = this.loggingService.getLogger("[PrinterService]");
      const methodName = "ctor";
      this.logger.entry(methodName);
  }

  private changesListenerStart(printer): void {
    // console.log('## change START');
    // this.patchObserver = jsonPatch.observe(printer, null);
  }

  private changesListenerStop(printer): void {
    // console.log('## change STOP');
    // if (this.patchObserver && printer) {
    // jsonPatch.unobserve<IPrinter>(printer, this.patchObserver);
    // }
  }

  private deserializeLinkObjects(printer: IPrinter, links: any): void {
    if (!links) {
      return;
    }

    const linkMappings: Array<{ key: string; target: (url: string | null) => void }> = [
      { key: 'self', target: (url) => printer.links.self = url },
      { key: 'px:registerNfcTag', target: (url) => printer.links.registerNfcTag = url }
    ];

    linkMappings.forEach(({ key, target }) => {
      const href = links[key]?.href;
      if (href) {
        target(this.sanitizationService.validateApiUrl(href));
      }
    });
  }

  /**
   * Safely sanitizes non-display text fields from API responses to prevent XSS.
   * Strips HTML tags and dangerous characters. Use for fields NOT shown in templates.
   */
  private sanitizeTextField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeTextInput(value);
    return sanitized || null;
  }

  /**
   * Sanitizes user-provided text fields that will be displayed in templates.
   * Preserves all characters so they render as visible plain text.
   */
  private sanitizeDisplayField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeDisplayText(value);
    return sanitized || null;
  }

  private deserializeEmbeddedObjects(printer: IPrinter, embedded: any): void {
    if (embedded) {
      const embeddedParameters: IPrinterEmbedded = {
        printerModel: embedded['printerModel'] ? embedded['printerModel'] : null,
        queues: embedded['queues'] ? embedded['queues'] : [],
        status: embedded['status'] ? {
          errors: embedded['status']['errors'] ? embedded['status']['errors'] : [],
          lastPollTime: embedded['status']['lastPollTime'] ? embedded['status']['lastPollTime'] : null,
          lastSuccessfullPoll: embedded['status']['lastSuccessfullPoll'] ? embedded['status']['lastSuccessfullPoll'] : null,
          lastUsagePollTime: embedded['status']['lastUsagePollTime'] ? embedded['status']['lastUsagePollTime'] : null,
          links: embedded['status']['_links'] ? {
            self: embedded['status']['_links']['self'] ? embedded['status']['_links']['self']['href'] : null,
            monitorWs: embedded['status']['_links']['px:monitorWs'] ? embedded['status']['_links']['px:monitorWs']['href'] : null,
          } : null,
          pollStatus: embedded['status']['pollStatus'] ? embedded['status']['pollStatus'] : null,
          printerReportedState: embedded['status']['printerReportedState'] ? embedded['status']['printerReportedState'] : null,
          state: embedded['status']['state'] ? embedded['status']['state'] : null,
          tonerLevels: embedded['status']['tonerLevels'] ? embedded['status']['tonerLevels'] : null,
          warnings: embedded['status']['warnings'] ? embedded['status']['warnings'] : [],
        } : null
      };
      printer.embedded = embeddedParameters;
    }
  }

  public deserializePrinter(input: any, skipObservers: boolean): IPrinter {
    this.changesListenerStop(input);
    const printer: IPrinter = {
      links: {
        self: null,
        registerNfcTag: null
      },
      colorSupport: input.colorSupport ? input.colorSupport : false,
      created: input.created ? input.created : null,
      duplexSupport: input.duplexSupport ? input.duplexSupport : false,
      embedded: input._embedded ? input._embedded : null,
      geoLocation: input.geoLocation ? input.geoLocation : null,
      ipAddress: input.ipAddress ? (this.sanitizeTextField(input.ipAddress) as any) : null,
      location: input.location ? (this.sanitizeDisplayField(input.location) as any) : null,
      macAddress: input.macAddress ? (this.sanitizeTextField(input.macAddress) as any) : null,
      name: input.name ? (this.sanitizeDisplayField(input.name) as any) : null,
      pdls: input.pdls ? input.pdls : null,
      serialNumber: input.serialNumber ? (this.sanitizeTextField(input.serialNumber) as any) : null,
      signId: input.signId ? (this.sanitizeDisplayField(input.signId) as any) : null,
      type: input.type ? input.type : null,
      version: input.version ? input.version : 0,
      network: this.deserializeNetwork(input) ,
      queues: input._embedded ? this.formatQueuesArray(input._embedded) : [],
      options: {
        isAllPrintJobsSelected: false,
        allowToPrint: true,
        isSelectedToPrintOnIt: false,
        isFavorite: false
      }
    };
    if (input._links) {this.deserializeLinkObjects(printer, input._links);}
    if (input._embedded) {this.deserializeEmbeddedObjects(printer, input._embedded);}
    if (!skipObservers) {
      this.changesListenerStart(printer);
    }
    return printer;
  }

  private deserializeNetwork(input): string {
    if (input.type !== 'FREEDOM' && input._links && input._links['px:network'] && input._links['px:network'].href) {
      return input._links['px:network'].href;
    } else {
      return null;
    }
  }

  public formatQueuesArray (embedded): Array<IQueue> {

    const queusArray: Array<IQueue> = [];
    if (embedded.queues) {
      for (let i = 0; i < embedded.queues.length; i++) {
        const queue: IQueue = {
          links: {
            self: embedded.queues[i]._links.self.href ? embedded.queues[i]._links.self.href : null,
            printer: embedded.queues[i]._links['px:printer'].href ? embedded.queues[i]._links['px:printer'].href : null
          },
          active: embedded.queues[i].active ? embedded.queues[i].active : false,
          alwaysReprint: embedded.queues[i].alwaysReprint ? embedded.queues[i].alwaysReprint : false,
          autoInstall: embedded.queues[i].autoInstall ? embedded.queues[i].autoInstall : false,
          autoUpdate: embedded.queues[i].autoUpdate ? embedded.queues[i].autoUpdate : false,
          defaultDuplex: embedded.queues[i].defaultDuplex ? embedded.queues[i].defaultDuplex : false,
          defaultMono: embedded.queues[i].defaultMono ? embedded.queues[i].defaultMono : false,
          errors: [],
          global: embedded.queues[i].global ? embedded.queues[i].global : false,
          name: embedded.queues[i].name ? embedded.queues[i].name : null,
          signId: embedded.queues[i].signId ? embedded.queues[i].signId : null,
          groups: embedded.queues[i] ? this.formateGroupsArray(embedded.queues[i]) : [],
          isFavorite: embedded.queues[i].isFavorite ? embedded.queues[i].isFavorite : false,
          isDisabled: embedded.queues[i].isDisabled ? embedded.queues[i].isDisabled : false,
          isLastUsedQueue: embedded.queues[i].isLastUsedQueue ? embedded.queues[i].isLastUsedQueue : false,
          printerDetails: {
            location: null,
            colorSupport: false,
            duplexSupport: false
          },
        };
        queusArray.push(queue);
      }
    }
    return queusArray;
  }

  public formateGroupsArray (queue): Array<string> {
    const groupsArray: Array<string> = [];
    if (queue._links && queue._links['px:group']) {
      if (Array.isArray(queue._links['px:group'])) {
        for (let i = 0; i < queue._links['px:group'].length; i++) {
          groupsArray.push(queue._links['px:group'][i].href);
        }
      } else {
        groupsArray.push(queue._links['px:group'].href);
      }
    }
    return groupsArray;
  }

  private deserializePrinterList(input: any): Array<IPrinter> {
    const printerList: Array<IPrinter> = [];
    if (input._embedded && input._embedded['px:printerResources']) {
      for (const printer of input._embedded['px:printerResources']) {
        printerList.push(this.deserializePrinter(printer, true));
      }
    }
    printerList.push(this.printerListPageObject);
    return printerList;
  }

  public getPrinterList(parameters: any, urlString: string): Observable<Array<IPrinter>> {
    this.logger.info('getPrinterList()');
    const printerUrl = this.tenant().links.printers + '?' + urlString;
    return new Observable((observer) => {
      this.httpClient.post<any>(printerUrl, parameters)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error();
          observer.complete();
        })
      )))
      .subscribe((response: any) => {
        this.printerListPageObject = {
          page: response.page
        };
        observer.next(this.deserializePrinterList(response));
        observer.complete();
      });
    });
  }

  private deserializePrintQueue(input: any): IQueue {
    const queue: IQueue = {
      links: {
        self: this.sanitizationService.validateApiUrl(input._links?.self?.href),
        printer: this.sanitizationService.validateApiUrl(input._links?.['px:printer']?.href)
      },
      active: input.active ? input.active : false,
      alwaysReprint: input.alwaysReprint ? input.alwaysReprint : false,
      autoInstall: input.autoInstall ? input.autoInstall : false,
      autoUpdate: input.autoUpdate ? input.autoUpdate : false,
      defaultDuplex: input.defaultDuplex ? input.defaultDuplex : false,
      defaultMono: input.defaultMono ? input.defaultMono : false,
      errors: [],
      global: input.global ? input.global : false,
      name: this.sanitizeDisplayField(input.name),
      signId: this.sanitizeDisplayField(input.signId),
      isFavorite: input.isFavorite ? input.isFavorite : false,
      isDisabled: input.isDisabled ? input.isDisabled : false,
      isLastUsedQueue: input.isLastUsedQueue ? input.isLastUsedQueue : false,
      printerDetails: {
        location: this.sanitizeDisplayField(input.location),
        colorSupport: input.color ? input.color : false,
        duplexSupport: input.duplex ? input.duplex : false
      },
    };
    return queue;
  }

  private deserializePrintQueuesList(input: any): Array<IQueue> {
    const printQueuesList: Array<IQueue> = [];
    if (input._embedded && input._embedded['px:printerQueueResources']) {
      for (const queue of input._embedded['px:printerQueueResources']) {
        printQueuesList.push(this.deserializePrintQueue(queue));
      }
    }
    printQueuesList.push(this.printersFromQueuesRequest);
    printQueuesList.push(this.printerListPageObject);

    return printQueuesList;
  }

  public getPrinterQueuesList(parameters: any): Observable<Array<IQueue>> {
    this.logger.info('getPrinterQueuesList()');
    const queuesUrl = this.tenant().links.queues;
    return new Observable((observer) => {
      this.httpClient.post<any>(queuesUrl, parameters)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((response: any) => {
        this.printerListPageObject = {
          page: response.page
        };
        const printers: Array<IPrinter> = this.deserializePrinterList(response);
        printers.pop()['page'];
        this.printersFromQueuesRequest = {
          printers: printers
        };
        observer.next(this.deserializePrintQueuesList(response));
        observer.complete();
      });
    });
  }

  public resetPrintersAllowToPrintProperty(printerList) {
    printerList.forEach(p => {
      p.options.allowToPrint = true;
      p.options.allPrintJobsSelected = false;
    });
  }

  public refreshPrinterStatus(printer): Observable<any> {
    this.logger.info('refreshPrinterStatus()');
    const statusMethod = 'STATUS';
    return new Observable((observer) => {
      this.httpClient.get<any>(printer.embedded.status.links.self + "?pollDeviceFirst=true&method=" + statusMethod)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(1),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((response: any) => {
        printer.embedded.status = {
          errors: response['errors'] ? response['errors'] : [],
          lastPollTime: response['lastPollTime'] ? response['lastPollTime'] : null,
          lastSuccessfullPoll: response['lastSuccessfullPoll'] ? response['lastSuccessfullPoll'] : null,
          lastUsagePollTime: response['lastUsagePollTime'] ? response['lastUsagePollTime'] : null,
          links: response['_links'] ? {
            self: response['_links']['self'] ? response['_links']['self']['href'] : null,
            monitorWs: response['_links']['px:monitorWs'] ? response['_links']['px:monitorWs']['href'] : null,
          } : null,
          pollStatus: response['pollStatus'] ? response['pollStatus'] : null,
          printerReportedState: response['printerReportedState'] ? response['printerReportedState'] : null,
          state: response['state'] ? response['state'] : null,
          tonerLevels: response['tonerLevels'] ? response['tonerLevels'] : null,
          warnings: response['warnings'] ? response['warnings'] : [],
        };
        observer.next(printer);
        observer.complete();
      });
    });
  }

  public getSinglePrinter(PrinterUrl: string, printerUrlString: string): Observable<Array<IPrinter>> {
    this.logger.info('getSinglePrinter()');
    return new Observable((observer) => {
      if (PrinterUrl) { // Is there a last used printer
        const selectedPrinterParameters: any = this.pagingService.getPrinterParameters();
        selectedPrinterParameters.printers = [];
        selectedPrinterParameters.printers.push(PrinterUrl);
        this.getPrinterList(selectedPrinterParameters, printerUrlString)
        .subscribe((selectedPrinter: Array<IPrinter>) => {
          observer.next(selectedPrinter);
          observer.complete();
        }, (error) => {
          observer.error();
          observer.complete();
        });
      } else { // no last used printer
        observer.next([]);
        observer.complete();
      }
    });
  }

  public getSingleQueue(queueUrl: string): Observable<IQueue> {
    this.logger.info('getSingleQueue()');
    return new Observable((observer) => {
      this.httpClient.get<IQueue>(queueUrl)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((queue) => {
        observer.next(this.deserializePrintQueue(queue));
        observer.complete();
      });
    });
  }

  public getPrintersWithPrintJobs(printJobs: Array<IPrintJob>, printerUrlString: string): Observable<Array<IPrinter>> {
    this.logger.info('getPrintersWithPrintJobs()');
    const printers: Array<IPrinter> = [];
    return new Observable((observer) => {
      if (printJobs.length !== 0) {
        const printerParameters = this.pagingService.getPrinterParameters();

        printerParameters.printers = [];
        printerParameters.signIds = [];

        printJobs.forEach(pj => { // get the printers that has printJobs
          if (printerParameters.printers.indexOf(pj.links.printer) === -1) {
            printerParameters.printers.push(pj.links.printer);
          }
        });

        this.getPrinterList(printerParameters, printerUrlString)
        .subscribe((printerList: Array<IPrinter>) => {
          const pagingObjectIndex = printerList.length -1;
          printerList.splice(pagingObjectIndex, 1); // remove the paging object from the list


          observer.next(printerList);
          observer.complete();
        });
      } else {
        observer.next(printers);
        observer.complete();
      }
    });
  }
}